import RouteSeo from "@/components/RouteSeo";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, useRef, lazy, Suspense } from "react";
import Index from "./pages/Index";
import CookieBanner from "@/components/CookieBanner";
import { hasMarketingConsent } from "@/lib/consent";
const CookiesPage = lazy(() => import("./pages/CookiesPage"));

const AboutPage = lazy(() => import("./pages/AboutPage"));
const ServiceProdej = lazy(() => import("./pages/ServiceProdej"));
const ServiceOdhad = lazy(() => import("./pages/ServiceOdhad"));
const ServiceOdhadByt = lazy(() => import("./pages/ServiceOdhadByt"));
const ServiceOdhadDum = lazy(() => import("./pages/ServiceOdhadDum"));
const ServiceOdhadPozemek = lazy(() => import("./pages/ServiceOdhadPozemek"));
const ServiceDedictvi = lazy(() => import("./pages/ServiceDedictvi"));
const ServiceAukce = lazy(() => import("./pages/ServiceAukce"));
const PropertyDetail = lazy(() => import("./pages/PropertyDetail"));
const ReferencePage = lazy(() => import("./pages/ReferencePage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const ArticlesPage = lazy(() => import("./pages/ArticlesPage"));
const ArticleDetail = lazy(() => import("./pages/ArticleDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));

const scrollPositions: Record<string, number> = {};

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const prevPath = useRef(pathname);

  useEffect(() => {
    // Save scroll position of the page we're leaving
    scrollPositions[prevPath.current] = window.scrollY;
    prevPath.current = pathname;

    // If navigating with a hash, scroll to the element
    if (hash) {
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
      return;
    }

    // If returning to index and we have a saved position, restore it
    if (pathname === "/" && scrollPositions["/"] != null) {
      setTimeout(() => window.scrollTo(0, scrollPositions["/"]), 0);
      return;
    }

    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return null;
};

const MetaPixelRouter = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    if (window.fbq && hasMarketingConsent()) {
      window.fbq('track', 'PageView');
    }
  }, [pathname]);

  return null;
};

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <MetaPixelRouter />
        <RouteSeo />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/o-mne" element={<AboutPage />} />
            <Route path="/sluzby/prodej" element={<ServiceProdej />} />
            <Route path="/sluzby/odhad" element={<ServiceOdhad />} />
            <Route path="/sluzby/odhad/byt" element={<ServiceOdhadByt />} />
            <Route path="/sluzby/odhad/dum" element={<ServiceOdhadDum />} />
            <Route path="/sluzby/odhad/pozemek" element={<ServiceOdhadPozemek />} />
            <Route path="/sluzby/dedictvi" element={<ServiceDedictvi />} />
            <Route path="/sluzby/aukce" element={<ServiceAukce />} />
            <Route path="/nemovitost/:slug" element={<PropertyDetail />} />
            <Route path="/reference" element={<ReferencePage />} />
            <Route path="/kontakt" element={<ContactPage />} />
            <Route path="/clanky" element={<ArticlesPage />} />
            <Route path="/clanky/:slug" element={<ArticleDetail />} />
            <Route path="/cookies" element={<CookiesPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <CookieBanner />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
